import './styles/theme.css';
import { getAllNotes, saveNote, deleteNote as dbDeleteNote, updateCardState } from './services/db.js';
import { scheduleNext } from './services/sm2.js';
import { createMobileEditor } from './editor/codemirror-setup.js';
import {
  toggleTaskAction,
  toggleHeadingAction,
  insertMathBlockAction,
  toggleInlineWrapAction,
  insertProjectPillAction
} from './editor/formatting-actions.js';
import { renderHeader } from './components/Header.js';
import { renderFormattingToolbar } from './components/FormattingToolbar.js';
import { renderPeekStrip } from './components/PeekStrip.js';
import { renderOverviewSheet } from './components/OverviewSheet.js';
import { renderReminderModal } from './components/ReminderModal.js';
import { renderFlashcardReviewModal } from './components/FlashcardReviewModal.js';
import { renderAuthModal } from './components/AuthModal.js';
import { initFirebase, syncNoteToFirebase, deleteNoteFromFirebase } from './services/firebase.js';
import { attachDeckGestures } from './gestures/deck-gestures.js';

// Application State
let notes = [];
let activeIndex = 0; // 0 = Blank Composer Draft, 1..N = existing notes
let composerId = crypto.randomUUID();
let composerBackingId = null;
let showOverview = false;
let showReminderModal = false;
let showReviewModal = false;
let showAuthModal = false;
let currentUser = null;
let isConnected = false;
let autocompleteQuery = null;
let isDarkMode = false;
let editorView = null;
let saveDebounceTimer = null;

// Programmatic content swaps (note switching) must not be mistaken for user
// edits — mirrors the desktop app's suppressContentChange (root CLAUDE.md #41).
// Without it, every swipe writes the loaded note straight back to IndexedDB
// and Firebase.
let suppressContentChange = false;
const pendingSaves = new Map();
let peekRenderedCount = -1;

async function initApp() {
  const root = document.getElementById('app');
  root.innerHTML = `
    <div id="header-container"></div>
    
    <main class="deck-container" id="deck-container">
      <div class="deck-slide" id="active-editor-slide">
        <div class="editor-wrapper" id="editor-parent"></div>
      </div>
    </main>

    <div id="toolbar-container"></div>
    <div id="peek-strip-container"></div>
    <div id="overview-container"></div>
    <div id="reminder-modal-container"></div>
    <div id="review-modal-container"></div>
    <div id="auth-modal-container"></div>
    <div id="autocomplete-container"></div>
  `;

  // Register PWA Service Worker
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/sw.js').catch(err => console.warn('SW reg failed:', err));
  }

  // Load saved notes from IndexedDB
  notes = await getAllNotes();

  // Firebase Auth (Google sign-in, gates the app like the AuthModal `!user` state)
  // + Firestore sync (users/{uid}/notes), shared with the Mac app.
  initFirebase(handleRemoteNotesUpdated, handleSyncStatusChanged, handleAuthUpdated);

  // CodeMirror 6 Mobile Editor
  const editorParent = document.getElementById('editor-parent');
  editorView = createMobileEditor(editorParent, '', {
    onChange: handleContentChanged,
    onToggleTask: handleToggleTask,
    onProjectQuery: handleProjectQuery,
    onTaskRefClick: handleTaskRefClick,
    onProjectClick: handleProjectClick
  });

  attachDeckGestures(document.getElementById('deck-container'), {
    getIndex: () => activeIndex,
    getPageCount: () => notes.length + 1,
    getNoteAt,
    onCommit: switchToIndex
  });

  // Focus editor on cold launch if on blank composer
  if (activeIndex === 0) {
    setTimeout(() => editorView?.focus(), 200);
  }

  // Keyboard auto-hiding listeners
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportResize);
  }

  // Reminder Alarm Checker loop
  setInterval(checkUpcomingReminders, 15000);

  // A backgrounded tab may never run the debounce timer again.
  window.addEventListener('pagehide', flushPendingSaves);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushPendingSaves();
  });

  renderAll();
}

function handleContentChanged(newContent) {
  if (suppressContentChange) return;
  const activeNote = getActiveNote();
  
  if (activeIndex === 0) {
    // Blank Composer: materialize note on first non-empty keystroke
    if (!composerBackingId && newContent.trim().length > 0) {
      const newNote = {
        id: crypto.randomUUID(),
        content: newContent,
        isPinned: false,
        reminderAt: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      composerBackingId = newNote.id;
      notes.unshift(newNote);
      persistNote(newNote);
    } else if (composerBackingId) {
      const existing = notes.find(n => n.id === composerBackingId);
      if (existing) {
        existing.content = newContent;
        persistNote(existing);
      }
    }
  } else if (activeNote) {
    activeNote.content = newContent;
    persistNote(activeNote);
  }
}

// Debounced save. Keyed by note id rather than a single pending slot: the old
// version cleared the shared timer on every call, so editing note A and then
// switching to note B within the debounce window dropped A's write entirely.
function persistNote(note) {
  pendingSaves.set(note.id, note);
  clearTimeout(saveDebounceTimer);
  saveDebounceTimer = setTimeout(flushPendingSaves, 100);
}

function flushPendingSaves() {
  clearTimeout(saveDebounceTimer);
  if (pendingSaves.size === 0) return;
  const batch = Array.from(pendingSaves.values());
  pendingSaves.clear();
  batch.forEach(async (note) => {
    const saved = await saveNote(note);
    syncNoteToFirebase(saved || note);
  });
  // Only the note count is visible in the peek strip, so rebuilding it on every
  // debounced save was a DOM write per keystroke burst and per swipe.
  if (notes.length !== peekRenderedCount) renderPeekComponent();
}

// Merge a Firestore snapshot's notes into local state. bulkUpsertNotes() into
// IndexedDB already happened inside firebase.js before this fires; this just
// keeps the in-memory `notes` array (and therefore the UI) in sync with it.
function handleRemoteNotesUpdated(remoteNotes) {
  remoteNotes.forEach(remote => {
    const idx = notes.findIndex(n => n.id === remote.id);
    if (idx === -1) {
      notes.push(remote);
    } else if (new Date(remote.updatedAt) > new Date(notes[idx].updatedAt)) {
      notes[idx] = remote;
    }
  });
  notes.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  renderAll();
}

function handleSyncStatusChanged(connected) {
  isConnected = connected;
  renderHeaderComponent();
}

function handleAuthUpdated(user) {
  currentUser = user;
  if (!user) showAuthModal = true;
  renderAuthComponent();
  renderHeaderComponent();
}

function renderAuthComponent() {
  renderAuthModal(document.getElementById('auth-modal-container'), {
    isOpen: showAuthModal,
    user: currentUser,
    onClose: () => {
      showAuthModal = false;
      renderAuthComponent();
    },
    onLoginSuccess: (user) => {
      currentUser = user;
      showAuthModal = false;
      renderAuthComponent();
      renderHeaderComponent();
    }
  });
}

function handleToggleTask(pos, isChecked) {
  if (!editorView) return;
  const currentDoc = editorView.state.doc.toString();
  const targetChar = isChecked ? 'x' : ' ';
  const updatedDoc = currentDoc.slice(0, pos) + targetChar + currentDoc.slice(pos + 1);
  
  editorView.dispatch({
    changes: { from: 0, to: currentDoc.length, insert: updatedDoc }
  });
}

function getNoteAt(index) {
  if (index === 0) {
    return composerBackingId ? notes.find(n => n.id === composerBackingId) : null;
  }
  return notes[index - 1] || null;
}

function getActiveNote() {
  return getNoteAt(activeIndex);
}

// The single path for changing pages. Flushes first: any pending edit belongs
// to the note we are leaving.
//
// Deliberately not renderAll(): only the header (pin and reminder state) and
// the peek dots depend on which page is active. renderAll() would also rebuild
// the formatting toolbar, which doesn't depend on the page at all, and clear
// five modal containers — per swipe, which shows up when paging quickly.
function switchToIndex(newIndex) {
  if (newIndex === activeIndex) return;
  flushPendingSaves();
  activeIndex = newIndex;
  renderHeaderComponent();
  renderPeekComponent();
  if (autocompleteQuery !== null) {
    autocompleteQuery = null;
    renderAutocompleteComponent();
  }
  loadActiveNoteContent();
}

// Project-name autocomplete: scans every note's "-> Name" occurrences into a
// deduped registry — a lightweight stand-in for desktop's LinkedTaskManager
// project index, sized for a bottom suggestion strip rather than a caret tooltip.
function getAllProjectNames() {
  const names = new Set();
  const projRegex = /->\s*([A-Za-z0-9_-]+)/g;
  notes.forEach(note => {
    let m;
    projRegex.lastIndex = 0;
    while ((m = projRegex.exec(note.content || '')) !== null) {
      names.add(m[1]);
    }
  });
  return Array.from(names);
}

function handleProjectQuery(query) {
  if (query === autocompleteQuery) return;
  autocompleteQuery = query;
  renderAutocompleteComponent();
}

function renderAutocompleteComponent() {
  const container = document.getElementById('autocomplete-container');
  if (autocompleteQuery === null) {
    container.innerHTML = '';
    return;
  }
  const matches = getAllProjectNames().filter(name =>
    name.toLowerCase().startsWith(autocompleteQuery.toLowerCase())
  );
  if (matches.length === 0) {
    container.innerHTML = '';
    return;
  }
  container.innerHTML = `
    <div class="autocomplete-strip">
      ${matches.map(name => `<button class="autocomplete-chip" data-name="${escapeHtml(name)}">${escapeHtml(name)}</button>`).join('')}
    </div>
  `;
  container.querySelectorAll('.autocomplete-chip').forEach(btn => {
    btn.onclick = () => {
      editorView?.acceptProjectSuggestion?.(btn.dataset.name);
      autocompleteQuery = null;
      renderAutocompleteComponent();
    };
  });
}

// Task-reference pill tap: jump to whichever note contains the referenced
// task line, then scroll to and flash-highlight that exact line.
function handleTaskRefClick(taskId) {
  const idx = notes.findIndex(n => (n.content || '').includes(`^t-${taskId}`));
  if (idx === -1) return;
  switchToIndex(idx + 1);
  editorView?.scrollToTaskRef?.(taskId);
}

// Project-pill tap: jump to the note whose first line matches the project
// name (project = a note acting as a hub, the lightweight mobile-web
// equivalent of desktop's LinkedTaskManager project resolution).
function handleProjectClick(projectName) {
  const target = projectName.trim().toLowerCase();
  const idx = notes.findIndex(n => {
    const firstLine = (n.content || '').split('\n')[0].replace(/^#+\s*/, '').trim().toLowerCase();
    return firstLine === target;
  });
  if (idx === -1) return;
  switchToIndex(idx + 1);
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function renderAll() {
  updateDarkModeState();
  renderHeaderComponent();
  renderToolbarComponent();
  renderPeekComponent();
  renderOverviewComponent();
  renderReminderComponent();
  renderReviewComponent();
  renderAuthComponent();
  renderAutocompleteComponent();
}

function updateDarkModeState() {
  if (isDarkMode) {
    document.body.classList.add('dark-mode');
  } else {
    document.body.classList.remove('dark-mode');
  }

  const metaTheme = document.getElementById('meta-theme-color');
  if (metaTheme) metaTheme.setAttribute('content', isDarkMode ? '#1C1C1E' : '#FAF8F5');
}

function renderHeaderComponent() {
  const activeNote = getActiveNote();
  renderHeader(document.getElementById('header-container'), {
    activeNote,
    isConnected,
    user: currentUser,
    isDarkMode,
    onToggleDarkMode: () => {
      isDarkMode = !isDarkMode;
      renderAll();
    },
    onTogglePin: () => {
      if (activeNote) {
        activeNote.isPinned = !activeNote.isPinned;
        persistNote(activeNote);
        renderAll();
      }
    },
    onDeleteNote: async () => {
      if (activeNote) {
        await dbDeleteNote(activeNote.id);
        deleteNoteFromFirebase(activeNote.id);
        notes = notes.filter(n => n.id !== activeNote.id);
        if (activeIndex > 0) activeIndex = Math.max(0, activeIndex - 1);
        resetComposer();
        renderAll();
        loadActiveNoteContent();
      }
    },
    onOpenReminder: () => {
      showReminderModal = true;
      renderReminderComponent();
    },
    onOpenReview: () => {
      showReviewModal = true;
      renderReviewComponent();
    },
    onOpenAccount: () => {
      showAuthModal = true;
      renderAuthComponent();
    }
  });
}

function renderToolbarComponent() {
  renderFormattingToolbar(document.getElementById('toolbar-container'), {
    onFormatAction: (actionType) => {
      if (!editorView) return;
      editorView.focus();

      switch (actionType) {
        case 'task':
          toggleTaskAction(editorView);
          break;
        case 'math':
          insertMathBlockAction(editorView);
          break;
        case 'code':
          toggleInlineWrapAction(editorView, '`');
          break;
        case 'highlight':
          toggleInlineWrapAction(editorView, '==');
          break;
        case 'project':
          insertProjectPillAction(editorView);
          break;
        case 'heading':
          toggleHeadingAction(editorView);
          break;
      }
    }
  });
}

function renderPeekComponent() {
  peekRenderedCount = notes.length;
  renderPeekStrip(document.getElementById('peek-strip-container'), {
    totalPages: notes.length + 1,
    activeIndex,
    noteCount: notes.length,
    onOpenOverview: () => {
      showOverview = true;
      renderOverviewComponent();
    }
  });
}

function renderOverviewComponent() {
  renderOverviewSheet(document.getElementById('overview-container'), {
    isOpen: showOverview,
    notes,
    onClose: () => {
      showOverview = false;
      renderOverviewComponent();
    },
    onSelectNote: (noteId) => {
      const idx = notes.findIndex(n => n.id === noteId);
      if (idx !== -1) {
        // switchToIndex early-returns on an unchanged index, so re-render the
        // sheet ourselves in that case or picking the open note leaves it up.
        showOverview = false;
        if (idx + 1 === activeIndex) renderOverviewComponent();
        else switchToIndex(idx + 1);
      }
    }
  });
}

function renderReminderComponent() {
  renderReminderModal(document.getElementById('reminder-modal-container'), {
    isOpen: showReminderModal,
    activeNote: getActiveNote(),
    onClose: () => {
      showReminderModal = false;
      renderReminderComponent();
    },
    onSaveReminder: (isoString) => {
      const activeNote = getActiveNote();
      if (activeNote) {
        activeNote.reminderAt = isoString;
        persistNote(activeNote);
        renderAll();
      }
    }
  });
}

function renderReviewComponent() {
  renderFlashcardReviewModal(document.getElementById('review-modal-container'), {
    isOpen: showReviewModal,
    notes,
    onClose: () => {
      showReviewModal = false;
      renderReviewComponent();
    },
    onGrade: async (noteId, cardId, grade) => {
      const note = notes.find(n => n.id === noteId);
      const prevState = note?.cardStates?.[cardId] || null;
      const nextState = scheduleNext(prevState, grade);
      const updated = await updateCardState(noteId, cardId, nextState);
      if (updated && note) {
        note.cardStates = updated.cardStates;
        note.updatedAt = updated.updatedAt;
        syncNoteToFirebase(note);
      }
    }
  });
}

function loadActiveNoteContent() {
  if (!editorView) return;
  const activeNote = getActiveNote();
  const text = activeNote ? activeNote.content : '';

  // The explicit anchor is load-bearing: the markdown plugin un-hides markers
  // on the selection's line, and the swipe ghost sets the same anchor, so the
  // two must agree or markers visibly change at the swap.
  suppressContentChange = true;
  editorView.dispatch({
    changes: { from: 0, to: editorView.state.doc.length, insert: text },
    selection: { anchor: text.length }
  });
  suppressContentChange = false;
}

function resetComposer() {
  composerId = crypto.randomUUID();
  composerBackingId = null;
}

function checkUpcomingReminders() {
  const now = new Date().getTime();
  notes.forEach(note => {
    if (note.reminderAt) {
      const remTime = new Date(note.reminderAt).getTime();
      if (remTime <= now && remTime > now - 60000) {
        if (Notification && Notification.permission === 'granted') {
          new Notification('FlowNote Reminder 🔔', {
            body: note.content.split('\n')[0] || 'Reminder for your note',
            icon: '/icon-192.png'
          });
        }
      }
    }
  });
}

function handleViewportResize() {
  const peekEl = document.querySelector('.peek-strip');
  const vv = window.visualViewport;
  if (!peekEl || !vv) return;
  
  const isKeyboardUp = window.innerHeight - vv.height > 150;
  if (isKeyboardUp) {
    peekEl.classList.add('keyboard-up');
  } else {
    peekEl.classList.remove('keyboard-up');
  }
}

// Launch app
initApp();
